/**
 * Blueprint Context
 * =================
 * Global React context for Blueprint configuration.
 * Makes config available throughout the entire application.
 * 
 * The frontend is a PURE RENDERER.
 * All content comes from Blueprint.
 * 
 * DATA SOURCE: SUPABASE ONLY
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getBlueprintConfig, getNestedValue, isSectionEnabled } from '../services/blueprintApi';

// Create context
const BlueprintContext = createContext(null);

// Default language
const DEFAULT_LANG = 'it';

// ============================================================================
// SAFE TEXT HELPER - Prevents {text, route} objects from being rendered
// ============================================================================

/**
 * Extract safe text from any value - CRITICAL for preventing React render errors
 * Handles: strings, numbers, {text, route} objects, null/undefined
 */
export const safeTextValue = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    // Handle {text, route} format
    if (value.text) return safeTextValue(value.text, fallback);
    if (value.label) return safeTextValue(value.label, fallback);
    if (value.title) return safeTextValue(value.title, fallback);
    if (value.name) return safeTextValue(value.name, fallback);
    // Don't stringify complex objects, return fallback
    console.warn('⚠️ safeTextValue: Unexpected object', value);
    return fallback;
  }
  return fallback;
};

/**
 * Blueprint Provider
 * Wraps the app and provides config to all components
 * 
 * CRITICAL: Must load ONCE, resolve correctly, never block the app
 * CRITICAL: Must NOT re-mount or cause loops
 */
export const BlueprintProvider = ({ children, initialLang = DEFAULT_LANG }) => {
  const [config, setConfig] = useState(null);
  const [lang, setLang] = useState(initialLang);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // CRITICAL: Refs to prevent re-mount loops
  const hasLoadedRef = useRef(false);
  const isMountedRef = useRef(true);
  const loadingRef = useRef(false);

  // Log mount - MUST appear only once
  useEffect(() => {
    console.log('🧠 BlueprintProvider MOUNTED');
    isMountedRef.current = true;
    
    return () => {
      console.log('🧠 BlueprintProvider UNMOUNTED');
      isMountedRef.current = false;
    };
  }, []);

  // Load configuration ONCE on mount
  useEffect(() => {
    // CRITICAL: Prevent multiple loads
    if (hasLoadedRef.current) {
      console.log('🧠 BlueprintProvider: Already loaded, skipping');
      return;
    }
    
    if (loadingRef.current) {
      console.log('🧠 BlueprintProvider: Already loading, skipping');
      return;
    }
    
    hasLoadedRef.current = true;
    loadingRef.current = true;
    
    const loadConfig = async () => {
      console.log('🧠 BlueprintProvider: Loading config for', lang);
      
      // Timeout safety
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current && loading) {
          console.warn('⚠️ BlueprintProvider: Timeout reached, using fallback');
          setConfig({});
          setLoading(false);
          setError('Config load timeout');
          loadingRef.current = false;
        }
      }, 5000);
      
      try {
        const data = await getBlueprintConfig(lang);
        
        if (isMountedRef.current) {
          console.log('✅ BlueprintProvider: Config loaded successfully');
          setConfig(data || {});
          setError(null);
        }
      } catch (err) {
        console.error('❌ BlueprintProvider: Error loading config', err);
        if (isMountedRef.current) {
          setError(err.message);
          setConfig({}); // Fallback to empty config
        }
      } finally {
        clearTimeout(timeoutId);
        if (isMountedRef.current) {
          setLoading(false);
          loadingRef.current = false;
        }
      }
    };
    
    loadConfig();
    
    // NO cleanup needed - we use refs
  }, []); // EMPTY DEPS - runs ONCE on mount

  // DISABLED: Language change reload (causes loops)
  // If language change is needed later, implement via explicit user action

  // Change language - DISABLED to prevent loops
  // TODO: Re-enable with proper debouncing if multilingual is needed
  const changeLanguage = useCallback((newLang) => {
    console.log('🌍 Language change requested:', newLang, '(DISABLED to prevent loops)');
    // DISABLED: setLang(newLang);
    // When re-enabled, must reload config manually
  }, []);

  // Refresh config - manual reload (use sparingly)
  const refreshConfig = useCallback(async () => {
    if (loadingRef.current) {
      console.log('🔄 Already loading, skipping refresh');
      return;
    }
    
    console.log('🔄 Refreshing Blueprint config');
    loadingRef.current = true;
    setLoading(true);
    
    try {
      const data = await getBlueprintConfig(lang);
      if (isMountedRef.current) {
        setConfig(data || {});
        setError(null);
      }
    } catch (err) {
      console.error('❌ Refresh error:', err);
      if (isMountedRef.current) {
        setError(err.message);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        loadingRef.current = false;
      }
    }
  }, [lang]);

  // Get value by path (e.g., 'homepage.hero.title')
  const getValue = useCallback((path, fallback = '') => {
    if (!config) return fallback;
    const value = getNestedValue(config, path);
    return value !== undefined ? value : fallback;
  }, [config]);

  // Get text value (same as getValue but for clarity)
  const getText = useCallback((path, fallback = '') => {
    return getValue(path, fallback);
  }, [getValue]);

  // Check if section is enabled
  const isEnabled = useCallback((sectionKey) => {
    return isSectionEnabled(sectionKey, config);
  }, [config]);

  // Get theme colors
  const colors = config?.ui?.theme?.colors || {
    gold: '#a98865',
    goldLight: '#c4a07a',
    cream: '#F5F2EE',
    dark: '#0D0D0D',
    darkBg: '#080807',
    text1: '#0D0D0D',
    text2: '#444444',
    text3: '#888888',
    border: '#E8E4DE'
  };

  // Get theme fonts
  const fonts = config?.ui?.theme?.fonts || {
    heading: 'Cormorant Garamond, serif',
    body: 'Inter, sans-serif'
  };

  // =========================================================================
  // 4-LAYER ARCHITECTURE HELPERS
  // =========================================================================
  
  // CONTENT LAYER - All texts
  const content = config?.content || {};
  const getContent = useCallback((path, fallback = '') => {
    return getValue(`content.${path}`, fallback);
  }, [getValue]);
  
  // UI LAYER - Layout and structure
  const uiConfig = config?.ui || {};
  const getUI = useCallback((path, fallback = null) => {
    return getValue(`ui.${path}`, fallback);
  }, [getValue]);
  
  // LOGIC LAYER - SRE and rules
  const logic = config?.logic || {};
  const getLogic = useCallback((path, fallback = null) => {
    return getValue(`logic.${path}`, fallback);
  }, [getValue]);
  
  // FEATURES LAYER - Module behavior
  const features = config?.features || {};
  const getFeature = useCallback((path, fallback = null) => {
    return getValue(`features.${path}`, fallback);
  }, [getValue]);
  
  // Check if a feature module is enabled
  const isFeatureEnabled = useCallback((moduleName) => {
    const feature = features[moduleName];
    return feature?.enabled !== false;
  }, [features]);
  
  // Get button text from common content
  const getButton = useCallback((key, fallback = '') => {
    return content?.common?.buttons?.[key] || fallback;
  }, [content]);
  
  // Get state message from common content
  const getState = useCallback((key, fallback = '') => {
    return content?.common?.states?.[key] || fallback;
  }, [content]);
  
  // =========================================================================
  // ROUTING LAYER - Config-driven navigation (NO HARDCODED ROUTES)
  // =========================================================================
  
  // Get routes config
  const routes = config?.ui?.routes || {};
  
  // Get home config (sections, order)
  const homeConfig = config?.ui?.home || {};
  
  // Get a route path by key (e.g., 'explorer' -> '/materials')
  const getRoute = useCallback((key, fallback = '/') => {
    return routes[key] || fallback;
  }, [routes]);
  
  // Build a route with params (e.g., 'material_detail', { slug: 'xyz' } -> '/materials/xyz')
  const buildRoute = useCallback((key, params = {}) => {
    let route = routes[key] || '/';
    
    // Replace :param placeholders
    Object.entries(params).forEach(([param, value]) => {
      route = route.replace(`:${param}`, value);
    });
    
    return route;
  }, [routes]);
  
  // Get navigation config
  const navigation = config?.ui?.navigation || {};
  
  // Get navbar links with routes resolved
  const getNavLinks = useCallback(() => {
    const navConfig = navigation.navbar || {};
    const links = navConfig.links || [];
    const labels = navConfig.labels || {};
    
    // Helper to extract text safely
    const extractText = (value, fallback) => {
      if (!value) return fallback;
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        return value.text || value.label || fallback;
      }
      return fallback;
    };
    
    return links.map(link => ({
      key: link.key,
      text: extractText(labels[link.key], link.key),
      url: routes[link.route] || `/${link.route}`
    }));
  }, [navigation, routes]);
  
  // Get entry points (4 independent flows)
  const getEntryPoints = useCallback(() => {
    return navigation.entry_points || {};
  }, [navigation]);

  // CRITICAL: Block initial render until config is loaded or timeout
  // This prevents child components from mounting before config is ready
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  useEffect(() => {
    // Mark initial load complete when:
    // 1. Config is loaded (loading becomes false)
    // 2. Or after 3 second timeout (graceful degradation)
    if (!loading) {
      setInitialLoadComplete(true);
    }
    
    const timeout = setTimeout(() => {
      if (!initialLoadComplete) {
        console.log('⏱️ Blueprint initial load timeout - proceeding with defaults');
        setInitialLoadComplete(true);
      }
    }, 3000);
    
    return () => clearTimeout(timeout);
  }, [loading, initialLoadComplete]);

  // Context value
  const value = {
    // Full config object
    config,
    
    // Current language
    lang,
    
    // Loading state
    loading,
    
    // Error state
    error,
    
    // Actions
    changeLanguage,
    refreshConfig,
    
    // Generic helpers
    getValue,
    getText,
    isEnabled,
    
    // 4-LAYER HELPERS
    getContent,
    getUI,
    getLogic,
    getFeature,
    isFeatureEnabled,
    getButton,
    getState,
    
    // ROUTING HELPERS (CRITICAL: no hardcoded routes)
    routes,
    navigation,
    getRoute,
    buildRoute,
    getNavLinks,
    getEntryPoints,
    
    // Layer objects (direct access)
    content,
    ui: uiConfig,
    logic,
    features,
    
    // Theme (from UI layer)
    colors,
    fonts,
    
    // CRITICAL: Homepage data comes from ui.home (DB structure)
    // Exposed as 'homepage' for backwards compatibility with components
    homepage: config?.ui?.home || {},
    
    // Backwards compatibility - existing sections
    navbar: config?.ui?.navigation?.navbar || config?.navbar || {},
    footer: config?.ui?.footer || config?.footer || {},
    sre: config?.logic?.sre || config?.sre || {}
  };

  // STRUCTURAL FIX: Block children until initial load is complete
  // This prevents remounting of child components when config arrives
  if (!initialLoadComplete) {
    return (
      <BlueprintContext.Provider value={value}>
        <div className="fixed inset-0 bg-[#0a0a0f] flex items-center justify-center z-[9999]">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-[#b08b5c]/30 border-t-[#b08b5c] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/50 text-sm font-light tracking-wider">Caricamento...</p>
          </div>
        </div>
      </BlueprintContext.Provider>
    );
  }

  return (
    <BlueprintContext.Provider value={value}>
      {children}
    </BlueprintContext.Provider>
  );
};

/**
 * useBlueprint Hook
 * Access Blueprint config from any component
 */
export const useBlueprint = () => {
  const context = useContext(BlueprintContext);
  
  if (!context) {
    throw new Error('useBlueprint must be used within a BlueprintProvider');
  }
  
  return context;
};

/**
 * withBlueprint HOC
 * Wrap class components with Blueprint config
 */
export const withBlueprint = (Component) => {
  return function BlueprintComponent(props) {
    const blueprint = useBlueprint();
    return <Component {...props} blueprint={blueprint} />;
  };
};

/**
 * BlueprintText Component
 * Render text from Blueprint config
 * 
 * Usage: <BlueprintText path="homepage.hero.title" />
 */
export const BlueprintText = ({ path, fallback = '', as: Component = 'span', ...props }) => {
  const { getText } = useBlueprint();
  let text = getText(path, fallback);
  
  // CRITICAL: Handle {text, route} objects - extract text only
  if (typeof text === 'object' && text !== null) {
    text = text.text || text.label || text.title || fallback;
  }
  
  // Ensure we have a string
  if (typeof text !== 'string') {
    text = fallback;
  }
  
  // Handle newlines in text
  if (text.includes('\n')) {
    return (
      <Component {...props}>
        {text.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < text.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </Component>
    );
  }
  
  return <Component {...props}>{text}</Component>;
};

/**
 * BlueprintSection Component
 * Conditionally render section based on Blueprint config
 * 
 * Usage: <BlueprintSection name="hero">{...}</BlueprintSection>
 */
export const BlueprintSection = ({ name, children, fallback = null }) => {
  const { isEnabled } = useBlueprint();
  
  if (!isEnabled(name)) {
    return fallback;
  }
  
  return children;
};

/**
 * BlueprintCTA Component
 * Render CTA button/link from Blueprint config
 * 
 * Usage: <BlueprintCTA section="hero" variant="primary" />
 */
export const BlueprintCTA = ({ section, variant = 'primary', className = '', onClick }) => {
  const { homepage, getRoute } = useBlueprint();
  
  const sectionConfig = homepage?.[section] || {};
  
  // CTA can be string or {text, route} object - handle both
  const ctaValue = sectionConfig.cta || sectionConfig.linkText || 'Click';
  const ctaText = typeof ctaValue === 'object' 
    ? (ctaValue.text || ctaValue.label || 'Click')
    : String(ctaValue);
  
  const ctaLink = typeof ctaValue === 'object'
    ? (ctaValue.route || ctaValue.link || sectionConfig.ctaLink || '/')
    : (sectionConfig.ctaLink || sectionConfig.link || '/');
  
  // Resolve route
  const href = ctaLink.startsWith('/') ? ctaLink : getRoute(ctaLink, '/');
  
  const baseClass = variant === 'primary' ? 'sr-btn-primary' : 'sr-btn-ghost';
  
  if (onClick) {
    return (
      <button onClick={onClick} className={`${baseClass} ${className}`}>
        {ctaText}
      </button>
    );
  }
  
  return (
    <a href={href} className={`${baseClass} ${className}`}>
      {ctaText}
    </a>
  );
};

/**
 * useBlueprintSection Hook
 * Get section config with defaults
 * 
 * Usage: const { visible, title, items } = useBlueprintSection('discovery');
 */
export const useBlueprintSection = (sectionName) => {
  const { homepage, getUI, isEnabled } = useBlueprint();
  
  const homeConfig = getUI('home', {});
  const sections = homeConfig.sections || {};
  
  // Check if section is visible
  const visible = sections[sectionName] !== false;
  
  // Get section content from homepage
  const sectionContent = homepage?.[sectionName] || {};
  
  return {
    visible,
    enabled: visible,
    ...sectionContent
  };
};

export default {
  BlueprintProvider,
  useBlueprint,
  withBlueprint,
  BlueprintText,
  BlueprintSection,
  BlueprintCTA,
  useBlueprintSection,
  safeTextValue
};
